import { useEffect, useState } from 'react';
import type { ChangeEvent, VoidFunctionComponent } from 'react';

import { DEFAULT_VCS_APP, VCS_APP_DISPLAY } from '../../../../../shared/vcs/githubApp/apps';
import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useGitHubAppRepositories';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { useUninstallGitHubApp } from '../../../../hooks/api/vcs/githubApp/useUninstallGitHubApp';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import ServiceImportHeader from '../ServiceImportHeader';
import ServiceImportRepositories from '../ServiceImportRepositories';
import { ServiceMeta } from '../types';
import type { Repository, ServiceQueryParameters } from '../../../../types';
import type { OnSelectRepositoryFn } from '../HybridProjectsTable/types';

import styles from '../ServiceImport.module.less';

const SERVICE_DETAILS = ServiceMeta['github-app'];
const PAGE_SIZE = 30;
// Matches the legacy import flow: pause between keystrokes before searching server-side.
const SEARCH_DEBOUNCE_MS = 750;
const DEFAULT_APP_DISPLAY = VCS_APP_DISPLAY[DEFAULT_VCS_APP];

const noop = () => {};
const asyncNoop = async () => {};

/** The search/import controls read `q` and `page`; the rest of the query shape is unused here. */
const buildQuery = (q: string, page: number): ServiceQueryParameters => ({
  q,
  page,
  pageSize: PAGE_SIZE,
  filterType: '',
  filterValue: '',
  instanceName: '',
});

type ImportHeaderProps = {
  isInstalled: boolean;
  isLoading: boolean;
  searchValue: string;
  page: number;
  selectedRepoLength: number;
  onSearchChange: (ev: ChangeEvent<HTMLInputElement>) => void;
};

/**
 * The service label/frame reused from the legacy import layout. Once the app is installed the
 * search control appears; repository import lands in a follow-up ticket, so the import buttons
 * stay inert for now (selection is wired, but there is no import action behind it yet).
 */
const ImportHeader: VoidFunctionComponent<ImportHeaderProps> = ({
  isInstalled,
  isLoading,
  searchValue,
  page,
  selectedRepoLength,
  onSearchChange,
}) => (
  <div className="panel">
    <div className="panel-body padded padded-small">
      <ServiceImportHeader
        serviceDetails={SERVICE_DETAILS}
        filters={[]}
        query={buildQuery(searchValue, page)}
        instanceNames={[]}
        instanceIsConnected={isInstalled}
        shouldDisplayImportControls={isInstalled}
        selectedRepoLength={selectedRepoLength}
        isLoading={isLoading}
        searchValue={searchValue}
        onSelectInstanceName={asyncNoop}
        onSelectFilter={asyncNoop}
        onSearchChange={onSearchChange}
        onImport={asyncNoop}
        onBulkImport={noop}
        onConnectToService={noop}
      />
    </div>
  </div>
);

/** Left column: the auth-note/docs card and, once installed, a disconnect action. */
const ImportSidebar: VoidFunctionComponent<{ isInstalled: boolean }> = ({ isInstalled }) => {
  const { onUninstall, isUninstalling } = useUninstallGitHubApp();

  return (
    <div className="col-xs-3">
      {(SERVICE_DETAILS.authNote || SERVICE_DETAILS.docsURL) && (
        <div className="card card-flat">
          <IconText icon={icons.questionCircle} text={SERVICE_DETAILS.authNote as string} />
          {SERVICE_DETAILS.docsURL && (
            <a
              className="btn btn-default btn-block margin-top"
              href={SERVICE_DETAILS.docsURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.docsLink}>
                <IconText icon={icons.book} text={SERVICE_DETAILS.docsLinkText || 'Documentation'} />
              </span>
            </a>
          )}
        </div>
      )}
      {isInstalled && (
        <Button
          type={BUTTON_TYPE.ERROR}
          size={BUTTON_SIZE.SMALL}
          icon={icons.powerOff}
          isDisabled={isUninstalling}
          onClick={() => onUninstall(DEFAULT_VCS_APP)}
        >
          Disconnect from {SERVICE_DETAILS.displayName}
        </Button>
      )}
    </div>
  );
};

type RepositoriesColumnProps = {
  isInstalled: boolean;
  repositories: Repository[];
  selectedRepos: Repository[];
  page: number;
  isLoading: boolean;
  isError: boolean;
  onPageUp: () => Promise<void>;
  onPageDown: () => Promise<void>;
  onSelectRepository: OnSelectRepositoryFn;
};

/** Right column: the shared repositories table. Not installed → install CTA; installed → repos. */
const RepositoriesColumn: VoidFunctionComponent<RepositoriesColumnProps> = ({
  isInstalled,
  repositories,
  selectedRepos,
  page,
  isLoading,
  isError,
  onPageUp,
  onPageDown,
  onSelectRepository,
}) => {
  const startInstall = useStartGitHubAppInstall();

  return (
    <div className="col-xs-9">
      <ServiceImportRepositories
        repositories={repositories}
        selectedRepos={selectedRepos}
        page={page}
        pageSize={PAGE_SIZE}
        // vcs-service has a single "installed" state and no separate OAuth
        // "connect the service" step, so isConnected is always satisfied here.
        // hasInstallations is the real gate: false → install CTA, true → repos.
        isConnected
        hasInstallations={isInstalled}
        isLoading={isLoading}
        error={isError ? 'Please try again.' : undefined}
        onPageUp={onPageUp}
        onPageDown={onPageDown}
        onBranchChange={noop}
        onRowRedirect={noop}
        onSelectRepository={onSelectRepository}
        onImportRepository={noop}
        onConnectToService={noop}
        onInstallGithubApp={() => startInstall.mutate(DEFAULT_VCS_APP)}
        githubAppDisplay={DEFAULT_APP_DISPLAY}
      />
    </div>
  );
};

type ImportBodyProps = RepositoriesColumnProps & {
  isInstallationLoading: boolean;
  isInstallationError: boolean;
};

/** Body below the header: resolves the installation loading / error / connected states. */
const ImportBody: VoidFunctionComponent<ImportBodyProps> = ({
  isInstallationLoading,
  isInstallationError,
  isInstalled,
  repositories,
  selectedRepos,
  page,
  isLoading,
  isError,
  onPageUp,
  onPageDown,
  onSelectRepository,
}) => {
  if (isInstallationLoading) {
    return <Loader />;
  }
  if (isInstallationError) {
    return <ErrorAlert status={{ error: 'Failed to load GitHub App status. Please try again.' }} />;
  }
  return (
    <div className="row">
      <ImportSidebar isInstalled={isInstalled} />
      <RepositoriesColumn
        isInstalled={isInstalled}
        repositories={repositories}
        selectedRepos={selectedRepos}
        page={page}
        isLoading={isLoading}
        isError={isError}
        onPageUp={onPageUp}
        onPageDown={onPageDown}
        onSelectRepository={onSelectRepository}
      />
    </div>
  );
};

/**
 * Entry container for the vcs-service GitHub App import flow: an org installs the default
 * app here (step one of import, not a settings action), then browses its repositories.
 * Reuses the legacy header + repositories components for layout parity. The repository
 * listing paginates and searches server-side; repository import lands in a follow-up ticket.
 */
export const VcsGitHubAppImport: VoidFunctionComponent = () => {
  const { data: installation, isLoading: isInstallationLoading, isError: isInstallationError } =
    useGitHubAppInstallation(DEFAULT_VCS_APP);

  const isInstalled = Boolean(installation?.installed);

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRepos, setSelectedRepos] = useState<Repository[]>([]);
  const { debouncedValue: q } = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);

  // A new search starts over from the first page, matching the legacy import flow.
  useEffect(() => setPage(1), [q]);

  const {
    data: repositoryPage,
    isLoading: isRepositoriesLoading,
    isError: isRepositoriesError,
  } = useGitHubAppRepositories(DEFAULT_VCS_APP, { page, pageSize: PAGE_SIZE, q }, { enabled: isInstalled });

  // `VcsRepository` mirrors the legacy `Repository` field names for reuse; the only wire-shape
  // difference (`description: string | null`) is display-only, so the shared table consumes it directly.
  const repositories = (repositoryPage?.results ?? []) as Repository[];

  const onSearchChange = (ev: ChangeEvent<HTMLInputElement>) => setSearchValue(ev.target.value);
  const onPageUp = async () => setPage((current) => current + 1);
  const onPageDown = async () => setPage((current) => Math.max(1, current - 1));

  // Toggle a repo in/out of the selection. Keyed on `title` to match how the hybrid table
  // renders its checked state (repo names are unique within a single connection's account).
  // Selection persists across pages, so a repo picked on one page stays selected on another.
  const onSelectRepository = (repo: Repository) =>
    setSelectedRepos((current) =>
      current.some((selected) => selected.title === repo.title)
        ? current.filter((selected) => selected.title !== repo.title)
        : [...current, repo],
    );

  return (
    <div className="fossa-service-import">
      <ImportHeader
        isInstalled={isInstalled}
        isLoading={isRepositoriesLoading}
        searchValue={searchValue}
        page={page}
        selectedRepoLength={selectedRepos.length}
        onSearchChange={onSearchChange}
      />
      <ImportBody
        isInstallationLoading={isInstallationLoading}
        isInstallationError={isInstallationError}
        isInstalled={isInstalled}
        repositories={repositories}
        selectedRepos={selectedRepos}
        page={page}
        isLoading={isRepositoriesLoading}
        isError={isRepositoriesError}
        onPageUp={onPageUp}
        onPageDown={onPageDown}
        onSelectRepository={onSelectRepository}
      />
    </div>
  );
};
